import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { apiProjectCreate } from "@/lib/api";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function NewProjectModal({ open, onOpenChange, onCreated }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [refs, setRefs] = useState([]);
    const [busy, setBusy] = useState(false);

    const toggleRef = (L) => setRefs((rs) => rs.includes(L) ? rs.filter((x) => x !== L) : [...rs, L]);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error("Project name required"); return; }
        setBusy(true);
        try {
            const project = await apiProjectCreate({ name: name.trim(), description: description.trim(), doctrine_refs: refs });
            toast.success("Project initialized");
            onCreated?.(project);
            onOpenChange(false);
            setName(""); setDescription(""); setRefs([]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="new-project-modal"
                className="bg-tca-bg-panel/95 backdrop-blur-xl border border-tca-cyan/22 text-tca-ink max-w-lg"
                style={{ boxShadow: "0 0 48px rgba(0,219,255,0.18)" }}
            >
                <DialogTitle className="font-mono-tight text-[11px] uppercase tracking-[0.32em] text-tca-cyan/90">
                    // initialize new project
                </DialogTitle>
                <DialogDescription className="sr-only">Create a new active project.</DialogDescription>
                <div className="font-display text-2xl mt-1">New active build.</div>
                <p className="text-sm text-tca-ink-muted mt-1">Continuity registered to your operator account.</p>

                <form onSubmit={submit} className="mt-5 space-y-4" data-testid="new-project-form">
                    <div className="space-y-1.5">
                        <Label className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">Project name</Label>
                        <Input
                            value={name} onChange={(e) => setName(e.target.value)}
                            data-testid="new-project-name"
                            className="bg-tca-bg-deep/60 border-tca-ink-dim text-tca-ink focus-visible:ring-tca-cyan focus-visible:border-tca-cyan/60"
                            placeholder="e.g. Smithers ecosystem v2"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">Description</Label>
                        <Input
                            value={description} onChange={(e) => setDescription(e.target.value)}
                            data-testid="new-project-description"
                            className="bg-tca-bg-deep/60 border-tca-ink-dim text-tca-ink focus-visible:ring-tca-cyan focus-visible:border-tca-cyan/60"
                            placeholder="One-line summary"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">Doctrine references</Label>
                        <div className="grid grid-cols-13 gap-1" data-testid="new-project-refs">
                            {LETTERS.map((L) => {
                                const on = refs.includes(L);
                                return (
                                    <button
                                        key={L} type="button" onClick={() => toggleRef(L)}
                                        data-testid={`new-project-ref-${L}`}
                                        className={`
                                            font-mono-tight text-[10px] font-semibold py-1.5
                                            border transition-all
                                            ${on
                                                ? "border-tca-cyan text-tca-cyan-hi bg-tca-cyan/10 shadow-[0_0_8px_rgba(0,219,255,0.4)]"
                                                : "border-tca-ink-dim text-tca-ink-muted/55 hover:text-tca-cyan hover:border-tca-cyan/55"}
                                        `}
                                    >{L}</button>
                                );
                            })}
                        </div>
                    </div>
                    <button
                        type="submit" disabled={busy} data-testid="new-project-submit"
                        className="w-full inline-flex items-center justify-center gap-2 font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs py-3.5 border border-tca-cyan text-tca-cyan-hi bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)] hover:text-white hover:shadow-glow-cyan-lg rounded-full transition-all"
                    >
                        {busy && <Loader2 size={13} className="animate-spin" />}
                        Initialize Project
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
